import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Box,
  Typography,
  Stack,
  Tooltip,
  Dialog,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  HealthAndSafety,
  PersonOutline,
  Person,
  Male,
  Female,
  HomeWork as HomeWorkIcon,
  LocationOn,
  Videocam,
  Chat as ChatIcon,
  Home,
} from "@mui/icons-material";
import { useFormatter } from "../../utils/useFormatter";
import { useMemo, useState, useEffect } from "react";
import ScheduleCancelDialog from "./ScheduleCancelDialog";
import { useFhirData } from "@/utils/useFhirData";
import { useViewContext } from "@/contexts/ViewContext";
import { ScheduleQueueActions } from "./ScheduleQueueActions";
import { PatientDetails } from "./PatientDetails";
import theme from "@/configs/Theme";
import { useDatetime } from "@/utils/useDatetime";
import { currentFeatures } from "@/configs/Settings";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useMessagingTemplatesForSend } from "src-next/modules/crm-clinical/messaging";
import { useLocationContext } from "@/contexts/LocationContext";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";

import { DateFilterType } from "@/utils/interfaces";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

/** Tipos de atendimento reconhecidos */
type AttendanceKind = "presencial" | "teleatendimento" | "chat" | "domicilio";

/**
 * Detecta o tipo de atendimento a partir do recurso Location.
 * Usa a extension attendance-channel (novo) com fallback para virtual-service (legado).
 */
function getAttendanceKind(locationResource: any): AttendanceKind {
  const channel = locationResource?.extension?.find((ext: any) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;

  const isVirtual =
    locationResource?.extension?.find((ext: any) =>
      ext.url?.includes("virtual-service"),
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (isVirtual) return "teleatendimento";
  return "presencial";
}

/**
 * Ícone e cores por tipo — tokens do Theme.tsx.
 * Igual ao AttendanceTypeSelector, AccordionHeader e PatientDetails.
 */
const ATTENDANCE_KIND_CONFIG: Record<
  AttendanceKind,
  { icon: React.ReactNode; colorKey: string; bgKey: string; label: string }
> = {
  presencial: {
    icon: <LocationOn sx={{ fontSize: 16 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
    label: "Presencial",
  },
  teleatendimento: {
    icon: <Videocam sx={{ fontSize: 16 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
    label: "Teleatendimento",
  },
  chat: {
    icon: <ChatIcon sx={{ fontSize: 16 }} />,
    colorKey: "error700.main",
    bgKey: "error100.main",
    label: "Chat",
  },
  domicilio: {
    icon: <Home sx={{ fontSize: 16 }} />,
    colorKey: "warning700.main",
    bgKey: "warning100.main",
    label: "Atendimento a Domicílio",
  },
};

interface ScheduleQueueProps {
  bundle: any | undefined;
  setRefetch: any;
  loading?: boolean;
  filterType?: DateFilterType;
}

export const ScheduleQueue: React.FC<ScheduleQueueProps> = ({
  bundle,
  setRefetch,
  loading,
  filterType = "day",
}) => {
  const { extractAppointmentData } = useFhirData;

  const isAppointmentBundleEntry = (
    entry: fhir5.BundleEntry<
      fhir5.Appointment | fhir5.Patient | fhir5.Location
    >,
  ): entry is fhir5.BundleEntry<fhir5.Appointment> =>
    entry.resource?.resourceType === "Appointment";

  const isPatientBundleEntry = (entry: any): any => {
    return entry.resource?.resourceType === "Patient";
  };

  const isLocationBundleEntry = (
    entry: fhir5.BundleEntry<
      fhir5.Appointment | fhir5.Patient | fhir5.Location
    >,
  ): entry is fhir5.BundleEntry<fhir5.Location> =>
    entry.resource?.resourceType === "Location";

  const patientsFromBundle = bundle?.entry?.filter(isPatientBundleEntry);
  const locationsBundle = bundle?.entry?.filter(isLocationBundleEntry);
  const appointmentsBundle = bundle?.entry?.filter(isAppointmentBundleEntry);

  /**
   * Extrai o endereço de visita domiciliar (extension home-visit-address) de um Appointment.
   * Retorna null quando a extension não existe.
   */
  const extractHomeVisitAddress = (appointmentResource: any): fhir5.Address | null => {
    const ext = appointmentResource?.extension?.find((e: any) =>
      e.url?.includes("home-visit-address"),
    );
    return ext?.valueAddress ?? null;
  };

  /**
   * Formata um endereço FHIR em duas linhas para não alargar o card.
   * Linha 1: logradouro, número, complemento, bairro
   * Linha 2: cidade - estado, CEP
   */
  const formatHomeVisitAddress = (address: fhir5.Address): [string, string] => {
    // Linha 1 — detalhes da rua
    const line1Parts: string[] = [];
    if (address.line?.[0]) line1Parts.push(address.line[0]);
    if (address.line?.[1]) line1Parts.push(address.line[1]);
    if (address.line?.[2]) line1Parts.push(address.line[2]);
    if (address.district)  line1Parts.push(address.district);

    // Linha 2 — localização
    const line2Parts: string[] = [];
    const cityState = [address.city, address.state].filter(Boolean).join(" - ");
    if (cityState) line2Parts.push(cityState);
    if (address.postalCode) {
      const cep = address.postalCode.replace(/\D/g, "");
      line2Parts.push(cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : address.postalCode);
    }

    return [line1Parts.join(", "), line2Parts.join(", ")];
  };
  const { viewType } = useViewContext();
  const { formatDate } = useDatetime;

  // locationContext tem os recursos completos de Location (com extensões), ao contrário do bundle
  const { location: locationContext } = useLocationContext();
  const { patients: organizationPatients } = usePatientOrganizationContext();

  // Prioriza Patient do bundle; quando ausente, usa lista da organização (mesmo padrão do locationContext)
  const patientsBundle =
    patientsFromBundle?.length > 0 ? patientsFromBundle : organizationPatients;

  const { organizationId } = useOrganizationContext();
  const { templates: messagingTemplates } = useMessagingTemplatesForSend({
    organizationId,
  });

  const [selectedCard, setSelectedCard] = useState<number | null>(null);

  // Estado para armazenar dados dos tutores mapeados por ID do paciente
  // Formato: { [patientId]: { name: string, phone: string } }
  const [tutorsData, setTutorsData] = useState<
    Record<string, { name: string; phone: string; identifier: string }>
  >({});
  const [loadingTutors, setLoadingTutors] = useState(false);

  const selectedCardData =
    selectedCard !== null && appointmentsBundle
      ? [
          appointmentsBundle[selectedCard]?.resource,
          locationsBundle?.filter((locationEl: any) => {
            const selectedAppointment = appointmentsBundle[selectedCard];
            if (selectedAppointment) {
              return (
                locationEl.resource?.id ===
                selectedAppointment?.resource?.participant?.[1].actor?.reference?.split(
                  "/",
                )[1]
              );
            }
            return false;
          })?.[0]?.resource,
        ].filter((el) => el !== undefined)
      : [];

  const selectedCardTeleconsulting = useFhirData.virtualService(
    locationsBundle?.find((loc: any) =>
      loc?.resource?.id.includes(selectedCardData?.[1]?.id),
    )?.resource,
  );

  const appointments = appointmentsBundle?.map((el: any) => {
    const patient = patientsBundle?.find(
      (patientEl: any) =>
        patientEl.resource?.id ===
          el.resource?.participant?.[0].actor?.reference?.split("/")[1] &&
        patientEl?.resource?.name,
    );
    const locationId = locationsBundle?.filter(
      (locationEl: any) =>
        locationEl.resource?.id ===
        el.resource?.participant?.[1].actor?.reference?.split("/")[1],
    );
    const auxPatient = patient;

    const auxData = {
      originalData: el?.resource,
      ...extractAppointmentData(el, auxPatient, locationId?.[0]),
    };

    return auxData;
  });
  const locationTeleconsulting = selectedCardTeleconsulting;
  const selectedAppointmentId =
    selectedCard !== null && appointmentsBundle
      ? appointmentsBundle[selectedCard]?.resource?.id
      : undefined;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [appointment, setAppointment] = useState<any>(null);

  const handleCancelAppointment = (appointment: any) => {
    setAppointment(appointment);
    setIsOpen(true);
  };
  const {
    formatDateTimeToHour,
    formatNameDisplay,
    formatDateTimeToWeekdayDate,
  } = useFormatter;

  // Agrupa os agendamentos por dia se o filtro for semana ou mês
  const groupedAppointments = appointments?.reduce(
    (acc: any, appointment: any) => {
      if (filterType === "day") {
        if (!acc["all"]) acc["all"] = [];
        acc["all"].push(appointment);
        return acc;
      }

      const dateKey = dayjs(appointment.startTime).format("YYYY-MM-DD");
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(appointment);
      return acc;
    },
    {},
  );

  // Ordena as chaves de data
  const sortedDateKeys = Object.keys(groupedAppointments || {}).sort();

  const onSelectAppointment = (appointment: any) => {
    const index = appointmentsBundle.findIndex(
      (el: any) => el.resource?.id === appointment.id,
    );
    setSelectedCard(index);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      cancelled: theme.palette.error.main,
      pending: theme.palette.warning500.main,
      booked: theme.palette.primary.main,
    } as const;
    return colors[status as keyof typeof colors] || "primary";
  };

  // Busca tutores para os pacientes quando não for defaultApp (modo vet)
  useEffect(() => {
    // Só busca tutores se não for defaultApp e houver appointments
    if (currentFeatures.defaultApp || !appointments?.length || loading) {
      return;
    }

    // Extrai IDs únicos dos pacientes dos appointments
    const patientIds = appointments
      .map((appointment: any) => {
        const patientId =
          appointment?.originalData?.participant?.[0].actor?.reference?.split(
            "/",
          )[1];
        return patientId;
      })
      .filter((id: string) => id && !tutorsData[id]); // Só busca se ainda não tiver em cache

    if (patientIds.length === 0) {
      return;
    }

    // Busca tutores para os pacientes
    const fetchTutors = async () => {
      setLoadingTutors(true);
      try {
        // Concatena os IDs com vírgula para buscar todos de uma vez
        const patientIdsParam = patientIds.join(",");

        const response = await GetRelatedPerson({
          patient: patientIdsParam,
          vetRelated: true, // Indica que é para sistema vet
        });

        if (response?.data?.entry) {
          // Mapeia os tutores por ID do paciente
          const tutorsMap: Record<
            string,
            { name: string; phone: string; identifier: string }
          > = {};

          response.data.entry.forEach((entry: any) => {
            const relatedPerson = entry.resource;
            if (relatedPerson?.patient?.reference) {
              // Extrai o ID do paciente da referência (formato: "Patient/ID")
              const patientRef = relatedPerson.patient.reference;
              const patientId = patientRef.replace("Patient/", "");

              // Extrai nome do tutor
              const tutorNameObj = relatedPerson.name?.find(
                (n: any) => n.use === "official" || n.use === "usual",
              );
              const tutorName = tutorNameObj?.text || "-";

              // Extrai telefone do tutor
              const tutorPhoneObj = relatedPerson.telecom?.find(
                (t: any) => t.system === "phone",
              );
              const tutorPhone = tutorPhoneObj?.value || "";

              const tutorIdentifier =
                relatedPerson?.identifier?.[0]?.value || "";

              if (patientId) {
                tutorsMap[patientId] = {
                  name: formatNameDisplay(tutorName) || "-",
                  phone: tutorPhone,
                  identifier: tutorIdentifier,
                };
              }
            }
          });

          // Atualiza o estado com os tutores encontrados
          setTutorsData((prev) => ({
            ...prev,
            ...tutorsMap,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar tutores:", error);
      } finally {
        setLoadingTutors(false);
      }
    };

    fetchTutors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments?.length, loading]);

  return (
    <>
      <List>
        {sortedDateKeys.map((dateKey) => {
          const groupAppointments = groupedAppointments[dateKey];

          return (
            <Box key={dateKey}>
              {filterType !== "day" && dateKey !== "all" && (
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    mb: 1,
                    mt: 2,
                    bgcolor: "primary.50",
                    borderRadius: 1,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CalendarTodayIcon color="primary" fontSize="small" />
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    sx={{ fontWeight: "bold", textTransform: "capitalize" }}
                  >
                    {dayjs(dateKey)
                      .locale("pt-br")
                      .format("dddd, DD [de] MMMM")}
                  </Typography>
                </Box>
              )}

              {groupAppointments?.map((appointment: any, index: number) => {
                const patient = patientsBundle?.find(
                  (patientEl: any) =>
                    patientEl.resource?.id ===
                    appointment?.originalData?.participant?.[0].actor?.reference?.split(
                      "/",
                    )[1],
                );

                // Usa locationContext (dados completos com extensões) para detectar
                // o tipo de atendimento — locationsBundle do bundle tem dados incompletos
                const locationEntry = locationContext?.find(
                  (loc: any) =>
                    loc?.resource?.id === (appointment.locationId as any)?.id,
                );
                const attendanceKind = getAttendanceKind(locationEntry?.resource);
                const attendanceConfig = ATTENDANCE_KIND_CONFIG[attendanceKind];
                return (
                  <ListItem
                    key={appointment.id}
                    sx={{
                      mb: index === groupAppointments.length - 1 ? 0 : 1,
                      bgcolor:
                        appointment.id === selectedAppointmentId
                          ? "action.selected"
                          : appointment.status === "proposed"
                            ? "warning100.main"
                            : "background.paper",
                      borderRadius: 1,
                      border:
                        appointment.status === "proposed"
                          ? "2px solid"
                          : "1px solid #ccc",
                      borderColor:
                        appointment.status === "proposed"
                          ? "warning500.main"
                          : getStatusColor(appointment.status) || "#ccc",
                      boxShadow: appointment.status === "proposed" ? 3 : 1,
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        bgcolor:
                          appointment.status === "proposed"
                            ? "warning200.main"
                            : "action.hover",
                      },
                      // Adiciona um pequeno indicador visual no canto superior direito
                      "&::before":
                        appointment.status === "proposed"
                          ? {
                              content: '""',
                              position: "absolute",
                              top: 8,
                              right: 8,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "warning500.main",
                            }
                          : {},
                    }}
                    onClick={() => onSelectAppointment(appointment)}
                  >
                    {/* Coluna esquerda: hora, data e tipo de atendimento */}
                    <Stack
                      direction="column"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ p: 2, minWidth: 110 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeIcon />
                        <Typography variant="caption">
                          {formatDateTimeToHour(appointment.startTime)}
                        </Typography>
                        {/* Ícone do tipo de atendimento logo após o horário */}
                        <Tooltip title={attendanceConfig.label}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: attendanceConfig.bgKey,
                              color: attendanceConfig.colorKey,
                              borderRadius: "50%",
                              width: 20,
                              height: 20,
                              flexShrink: 0,
                            }}
                          >
                            {attendanceConfig.icon}
                          </Box>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayIcon sx={{ fontSize: 20 }} />
                        <Typography variant="caption">
                          {formatDateTimeToWeekdayDate(appointment.startTime)}
                        </Typography>
                      </Stack>
                    </Stack>

                    <ListItemText
                      sx={{
                        borderLeft: 1,
                        borderColor: "divider",
                        pl: 2,
                      }}
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {viewType === "user" ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Avatar>
                                {appointment.patientName?.[0] || ""}
                              </Avatar>
                              <Stack>
                                <Typography variant="body1">
                                  {formatNameDisplay(appointment.patientName)}
                                </Typography>

                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography variant="body1">
                                    {patient?.resource?.birthDate &&
                                      `${formatDate(
                                        patient.resource.birthDate,
                                      )} - ${useFormatter.formatAge(
                                        patient.resource.birthDate,
                                      )} anos`}
                                  </Typography>

                                  {patient?.resource?.gender === "male" ? (
                                    <Male sx={{ fontSize: 20 }} />
                                  ) : (
                                    <Female sx={{ fontSize: 20 }} />
                                  )}
                                </Stack>
                              </Stack>
                            </Stack>
                          ) : (
                            <>
                              <Stack>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <PersonOutline color="primary" />
                                  <Typography variant="body1">
                                    {formatNameDisplay(appointment.patientName)}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Person color="primary" />
                                  <Typography variant="body1">
                                    {appointment.practitioner}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={
                                      appointment.healthcare || appointment.type
                                    }
                                    sx={{ height: 20 }}
                                  />
                                </Stack>
                              </Stack>
                            </>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {appointment.supportingInfo && (
                              <Tooltip title="Plano de Saúde">
                                <Chip
                                  size="small"
                                  label={appointment.supportingInfo[0]?.display}
                                  color="info"
                                  icon={<HealthAndSafety fontSize="small" />}
                                  sx={{ height: 20 }}
                                />
                              </Tooltip>
                            )}
                            {/* Exibe dados do tutor quando não for defaultApp (modo vet) */}
                            {!currentFeatures.defaultApp &&
                              patient?.resource?.id &&
                              tutorsData[patient.resource.id] && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: 12 }}
                                >
                                  {[
                                    `Tutor: ${tutorsData[patient.resource.id].name}`,
                                    tutorsData[patient.resource.id].phone &&
                                      useFormatter.formatPhoneNumber(
                                        tutorsData[patient.resource.id].phone,
                                      ),
                                  ]
                                    .filter(Boolean)
                                    .join(" | ")}
                                </Typography>
                              )}
                          </Stack>

                          {/* Endereço de visita domiciliar — exibido apenas quando a
                              extension home-visit-address estiver presente no Appointment.
                              Quebrado em duas linhas para não alargar o card. */}
                          {(() => {
                            const homeAddress = extractHomeVisitAddress(appointment.originalData);
                            if (!homeAddress) return null;
                            const [addressLine1, addressLine2] = formatHomeVisitAddress(homeAddress);
                            return (
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={0.5}
                                sx={{ mt: 0.5 }}
                              >
                                <HomeWorkIcon
                                  sx={{ fontSize: 15, color: "text.secondary", mt: "2px", flexShrink: 0 }}
                                />
                                <Box>
                                  {addressLine1 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: 12, lineHeight: 1.4 }}
                                    >
                                      {addressLine1}
                                    </Typography>
                                  )}
                                  {addressLine2 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ fontSize: 12, lineHeight: 1.4 }}
                                    >
                                      {addressLine2}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            );
                          })()}
                        </Box>
                      }
                    />

                    <Stack
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <ScheduleQueueActions
                        variant={"compact"}
                        appointment={appointment}
                        setRefetch={setRefetch}
                        locationTeleconsulting={!!locationTeleconsulting}
                        handleCancelAppointment={handleCancelAppointment}
                        patient={patient}
                        messagingTemplates={messagingTemplates}
                        tutorPhone={
                          !currentFeatures.defaultApp &&
                          patient?.resource?.id &&
                          tutorsData[patient.resource.id]
                            ? tutorsData[patient.resource.id].phone
                            : undefined
                        }
                        tutorCpf={
                          !currentFeatures.defaultApp &&
                          patient?.resource?.id &&
                          tutorsData[patient.resource.id]
                            ? tutorsData[patient.resource.id].identifier
                            : undefined
                        }
                      />
                    </Stack>
                  </ListItem>
                );
              })}
            </Box>
          );
        })}
      </List>

      {selectedCard !== null && (
        <Dialog
          maxWidth="md"
          open={selectedCard !== null}
          onClose={() => {
            setSelectedCard(null);
          }}
        >
          {/**
           * Importante: o modal deve usar a MESMA lista de templates já resolvida na agenda,
           * para evitar o `ScheduleQueueActions` abrir sem dados ou com templates divergentes.
           */}
          <PatientDetails
            setRefetch={setRefetch}
            onDeselectCard={() => {
              setSelectedCard(null);
            }}
            data={selectedCardData}
            handleCancelAppointment={handleCancelAppointment}
            patient={patientsBundle?.find(
              (patientEl: any) =>
                patientEl.resource?.id ===
                selectedCardData[0]?.participant?.[0].actor?.reference?.split(
                  "/",
                )[1],
            )}
            messagingTemplates={messagingTemplates}
            tutorPhone={
              currentFeatures.defaultApp
                ? undefined
                : (() => {
                    const patientId =
                      selectedCardData?.[0]?.participant?.[0].actor?.reference?.split(
                        "/",
                      )?.[1];
                    if (!patientId) return undefined;
                    return tutorsData[patientId]?.phone ?? undefined;
                  })()
            }
            tutorCpf={
              currentFeatures.defaultApp
                ? undefined
                : (() => {
                    const patientId =
                      selectedCardData?.[0]?.participant?.[0].actor?.reference?.split(
                        "/",
                      )?.[1];
                    if (!patientId) return undefined;
                    return tutorsData[patientId]?.identifier ?? undefined;
                  })()
            }
          />
        </Dialog>
      )}

      {isOpen && (
        <ScheduleCancelDialog
          appointment={appointment || selectedCardData[0]}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setRefetch={setRefetch}
          payment={[]}
          isPaid={false}
        />
      )}
    </>
  );
};
